import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AdSenseScript from '../../components/AdSenseScript';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LanguageSelector from './components/LanguageSelector';
import RegionSelector from './components/RegionSelector';
import CalculatorCard from './components/CalculatorCard';
import RegionalContext from './components/RegionalContext';
import AdPlacement from './components/AdPlacement';
import AdSenseUnit from '../../components/AdSenseUnit';

import SEOContent from './components/SEOContent';
import Footer from './components/Footer';
import { detectLanguage } from '../../utils/detectLanguage';

const LocalizedLandingPages = () => {
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [currentRegion, setCurrentRegion] = useState('us');

  useEffect(() => {
    // Detect language from URL path using safe utility
    const detectedLang = detectLanguage(location?.pathname);
    
    setCurrentLanguage(detectedLang);
    localStorage.setItem('preferredLanguage', detectedLang);
    localStorage.setItem('selectedLanguage', detectedLang);
    
    const regionMap = {
      en: 'us',
      es: 'es',
      pt: 'br',
      fr: 'fr',
      de: 'de'
    };
    setCurrentRegion(regionMap?.[detectedLang] || 'us');
  }, [location]);

  const handleLanguageChange = (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('preferredLanguage', langCode);
    
    const regionMap = {
      en: 'us',
      es: 'es',
      pt: 'br',
      fr: 'fr',
      de: 'de'
    };
    setCurrentRegion(regionMap?.[langCode] || 'us');
  };

  const handleRegionChange = (regionCode) => {
    setCurrentRegion(regionCode);
  };

  const pageContent = {
    en: {
      metaTitle: "Free Financial Calculators - Money Duration & Debt Freedom Tools",
      metaDescription: "Calculate how long your money will last and when you\'ll be debt-free. Free financial planning tools with instant results for smart money management.",
      welcomeTitle: "Smart Financial Planning Tools",
      welcomeSubtitle: "Get instant answers to your most urgent financial questions with our free calculators",
      calculators: [
        {
          title: "How Long Will My Money Last?",
          description: "Calculate exactly how long your savings will last based on your monthly expenses. Get clear status indicators and plan your financial future with confidence.",
          icon: "Clock",
          path: "/money-duration-calculator",
          features: [
            "Instant calculation of money duration",
            "Visual status indicators (SAFE/WARNING/CRITICAL)",
            "Exact run-out date with days and months remaining",
            "Mobile-optimized for quick access"
          ],
          ctaText: "Calculate Money Duration"
        },
        {
          title: "When Will I Be Debt-Free?",
          description: "Find out exactly when you\'ll be debt-free based on your total debt and monthly payments. Track your progress with clear status updates.",
          icon: "TrendingDown",
          path: "/debt-freedom-calculator",
          features: [
            "Calculate exact debt-free date",
            "Progress status tracking (ON TRACK/STRUGGLING/UNSUSTAINABLE)",
            "Months remaining until debt freedom",
            "Easy-to-use mobile interface"
          ],
          ctaText: "Calculate Debt Freedom"
        }
      ]
    },
    es: {
      metaTitle: "Calculadoras Financieras Gratuitas - Duración del Dinero y Libertad de Deudas",
      metaDescription: "Calcula cuánto durará tu dinero y cuándo estarás libre de deudas. Herramientas gratuitas de planificación financiera con resultados instantáneos.",
      welcomeTitle: "Herramientas Inteligentes de Planificación Financiera",
      welcomeSubtitle: "Obtén respuestas instantáneas a tus preguntas financieras más urgentes con nuestras calculadoras gratuitas",
      calculators: [
        {
          title: "¿Cuánto Durará Mi Dinero?",
          description: "Calcula exactamente cuánto durarán tus ahorros según tus gastos mensuales. Obtén indicadores de estado claros y planifica tu futuro financiero con confianza.",
          icon: "Clock",
          path: "/money-duration-calculator",
          features: [
            "Cálculo instantáneo de duración del dinero",
            "Indicadores de estado visuales (SEGURO/ADVERTENCIA/CRÍTICO)",
            "Fecha exacta de agotamiento con días y meses restantes",
            "Optimizado para móviles para acceso rápido"
          ],
          ctaText: "Calcular Duración del Dinero"
        },
        {
          title: "¿Cuándo Estaré Libre de Deudas?",
          description: "Descubre exactamente cuándo estarás libre de deudas según tu deuda total y pagos mensuales. Rastrea tu progreso con actualizaciones de estado claras.",
          icon: "TrendingDown",
          path: "/debt-freedom-calculator",
          features: [
            "Calcular fecha exacta libre de deudas",
            "Seguimiento del estado de progreso (EN CAMINO/LUCHANDO/INSOSTENIBLE)",
            "Meses restantes hasta la libertad de deudas",
            "Interfaz móvil fácil de usar"
          ],
          ctaText: "Calcular Libertad de Deudas"
        }
      ]
    },
    pt: {
      metaTitle: "Calculadoras Financeiras Gratuitas - Duração do Dinheiro e Liberdade de Dívidas",
      metaDescription: "Calcule quanto tempo seu dinheiro durará e quando você estará livre de dívidas. Ferramentas gratuitas de planejamento financeiro com resultados instantâneos.",
      welcomeTitle: "Ferramentas Inteligentes de Planejamento Financeiro",
      welcomeSubtitle: "Obtenha respostas instantâneas para suas perguntas financeiras mais urgentes com nossas calculadoras gratuitas",
      calculators: [
        {
          title: "Quanto Tempo Meu Dinheiro Durará?",
          description: "Calcule exatamente quanto tempo suas economias durarão com base em suas despesas mensais. Obtenha indicadores de status claros e planeje seu futuro financeiro com confiança.",
          icon: "Clock",
          path: "/money-duration-calculator",
          features: [
            "Cálculo instantâneo da duração do dinheiro",
            "Indicadores de status visuais (SEGURO/AVISO/CRÍTICO)",
            "Data exata de esgotamento com dias e meses restantes",
            "Otimizado para celular para acesso rápido"
          ],
          ctaText: "Calcular Duração do Dinheiro"
        },
        {
          title: "Quando Estarei Livre de Dívidas?",
          description: "Descubra exatamente quando você estará livre de dívidas com base em sua dívida total e pagamentos mensais. Acompanhe seu progresso com atualizações de status claras.",
          icon: "TrendingDown",
          path: "/debt-freedom-calculator",
          features: [
            "Calcular data exata livre de dívidas",
            "Rastreamento de status de progresso (NO CAMINHO/LUTANDO/INSUSTENTÁVEL)",
            "Meses restantes até a liberdade de dívidas",
            "Interface móvel fácil de usar"
          ],
          ctaText: "Calcular Liberdade de Dívidas"
        }
      ]
    },
    fr: {
      metaTitle: "Calculatrices Financières Gratuites - Durée de l\'Argent et Liberté de Dette",
      metaDescription: "Calculez combien de temps durera votre argent et quand vous serez libre de dettes. Outils gratuits de planification financière avec résultats instantanés.",
      welcomeTitle: "Outils Intelligents de Planification Financière",
      welcomeSubtitle: "Obtenez des réponses instantanées à vos questions financières les plus urgentes avec nos calculatrices gratuites",
      calculators: [
        {
          title: "Combien de Temps Durera Mon Argent?",
          description: "Calculez exactement combien de temps dureront vos économies en fonction de vos dépenses mensuelles. Obtenez des indicateurs de statut clairs et planifiez votre avenir financier en toute confiance.",
          icon: "Clock",
          path: "/money-duration-calculator",
          features: [
            "Calcul instantané de la durée de l\'argent",
            "Indicateurs de statut visuels (SÛR/AVERTISSEMENT/CRITIQUE)",
            "Date exacte d\'épuisement avec jours et mois restants",
            "Optimisé pour mobile pour un accès rapide"
          ],
          ctaText: "Calculer la Durée de l\'Argent"
        },
        {
          title: "Quand Serai-je Libre de Dettes?",
          description: "Découvrez exactement quand vous serez libre de dettes en fonction de votre dette totale et de vos paiements mensuels. Suivez vos progrès avec des mises à jour de statut claires.",
          icon: "TrendingDown",
          path: "/debt-freedom-calculator",
          features: [
            "Calculer la date exacte sans dette",
            "Suivi du statut de progression (SUR LA BONNE VOIE/EN DIFFICULTÉ/INSOUTENABLE)",
            "Mois restants jusqu\'à la liberté de dette",
            "Interface mobile facile à utiliser"
          ],
          ctaText: "Calculer la Liberté de Dette"
        }
      ]
    },
    de: {
      metaTitle: "Kostenlose Finanzrechner - Gelddauer und Schuldenfreiheit Tools",
      metaDescription: "Berechnen Sie, wie lange Ihr Geld reichen wird und wann Sie schuldenfrei sein werden. Kostenlose Finanzplanungstools mit sofortigen Ergebnissen.",
      welcomeTitle: "Intelligente Finanzplanungstools",
      welcomeSubtitle: "Erhalten Sie sofortige Antworten auf Ihre dringendsten Finanzfragen mit unseren kostenlosen Rechnern",
      calculators: [
        {
          title: "Wie Lange Wird Mein Geld Reichen?",
          description: "Berechnen Sie genau, wie lange Ihre Ersparnisse basierend auf Ihren monatlichen Ausgaben reichen werden. Erhalten Sie klare Statusindikatoren und planen Sie Ihre finanzielle Zukunft mit Vertrauen.",
          icon: "Clock",
          path: "/money-duration-calculator",
          features: [
            "Sofortige Berechnung der Gelddauer",
            "Visuelle Statusindikatoren (SICHER/WARNUNG/KRITISCH)",
            "Genaues Erschöpfungsdatum mit verbleibenden Tagen und Monaten",
            "Für Mobilgeräte optimiert für schnellen Zugriff"
          ],
          ctaText: "Gelddauer Berechnen"
        },
        {
          title: "Wann Werde Ich Schuldenfrei Sein?",
          description: "Finden Sie genau heraus, wann Sie schuldenfrei sein werden, basierend auf Ihrer Gesamtverschuldung und monatlichen Zahlungen. Verfolgen Sie Ihren Fortschritt mit klaren Statusaktualisierungen.",
          icon: "TrendingDown",
          path: "/debt-freedom-calculator",
          features: [
            "Genaues schuldenfreies Datum berechnen",
            "Fortschrittsstatus-Tracking (AUF KURS/KÄMPFEND/UNHALTBAR)",
            "Verbleibende Monate bis zur Schuldenfreiheit",
            "Benutzerfreundliche mobile Oberfläche"
          ],
          ctaText: "Schuldenfreiheit Berechnen"
        }
      ]
    }
  };

  const content = pageContent?.[currentLanguage] || pageContent?.en;

  return (
    <>
      <Helmet>
        <title>{content?.metaTitle}</title>
        <meta name="description" content={content?.metaDescription} />
        <meta name="google-adsense-account" content="ca-pub-2004114604148310" />
        <meta property="og:title" content={content?.metaTitle} />
        <meta property="og:description" content={content?.metaDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://financialcalculatornetwork.com/${currentLanguage !== 'en' ? currentLanguage + '/' : ''}`} />
        
        <link rel="alternate" hrefLang="en" href="https://financialcalculatornetwork.com/" />
        <link rel="alternate" hrefLang="es" href="https://financialcalculatornetwork.com/es/" />
        <link rel="alternate" hrefLang="pt" href="https://financialcalculatornetwork.com/pt/" />
        <link rel="alternate" hrefLang="fr" href="https://financialcalculatornetwork.com/fr/" />
        <link rel="alternate" hrefLang="de" href="https://financialcalculatornetwork.com/de/" />
        <link rel="alternate" hrefLang="x-default" href="https://financialcalculatornetwork.com/" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <AdSenseScript />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
                  {content?.welcomeTitle}
                </h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  {content?.welcomeSubtitle}
                </p>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <LanguageSelector 
                  currentLanguage={currentLanguage}
                  onLanguageChange={handleLanguageChange}
                />
                <RegionSelector 
                  currentRegion={currentRegion}
                  onRegionChange={handleRegionChange}
                  language={currentLanguage}
                />
              </div>
            </div>

            <RegionalContext region={currentRegion} language={currentLanguage} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
              {content?.calculators?.map((calculator, index) => (
                <CalculatorCard
                  key={index}
                  title={calculator?.title}
                  description={calculator?.description}
                  icon={calculator?.icon}
                  path={calculator?.path}
                  features={calculator?.features}
                  ctaText={calculator?.ctaText}
                />
              ))}
            </div>

            <AdPlacement position={1} />

            <SEOContent language={currentLanguage} />

            <AdSenseUnit />

            <AdPlacement position={2} />
          </div>
        </main>

        <AdPlacement position={3} />

        <Footer language={currentLanguage} />
      </div>
    </>
  );
};

export default LocalizedLandingPages;